import { useEffect, useState } from 'react'

import { useMutation, useQuery } from '@api/hooks'
import { Purchase as PurchaseRecord } from '@domain'
import { faCheck, faX } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Chip, Divider, Paper, Skeleton, Stack, Tab, Tabs, Typography } from '@mui/material'
import DirectDealApi, { DirectDealKeys } from '@services/directDeals.api'
import {
	DirectDealPurchaseCounts,
	DirectDealStatus,
	DirectDealSummary,
} from '@services/directDeals.types'
import { useQueryClient } from '@tanstack/react-query'
import { numToCurrency, numToPercentage } from '@utils/stringHelpers'
import { PurchaseCard } from '@views/MarketBaskets/ActionCenter/Components/PurchaseCard/PurchaseCard'
import { useMarketBasketContext } from '@views/MarketBaskets/MarketBaskets.hooks'
import { useParams } from 'react-router-dom'
import { StringParam, useQueryParam } from 'use-query-params'

import {
	addToTargetList,
	CategoryProps,
	removeFromPendingList,
	restoreCachesFromSnapshot,
	snapshotDirectDealCaches,
	updateAllList,
	updateDetailDealStatus,
	updateSummaryCache,
} from '../directDeals.utils'

type Props = {
	directDealId: Id
	onUpdateDirectDeal: (id: Id) => void
	// onMatchChange: () => void
}

type TabOptions = 'ALL' | 'STOCKED' | 'NOT_STOCKED'

export default function DirectDeal({ directDealId, onUpdateDirectDeal }: Props) {
	const [statusFilter] = useQueryParam('direct-deals', StringParam)
	const [activeTab, setActiveTab] = useState<TabOptions>('ALL')
	// const [comparePurchase, setComparePurchase] = useState<PurchaseRecord | null>(null)

	const { marketBasket } = useMarketBasketContext()
	const currency = marketBasket?.currency
	const queryClient = useQueryClient()
	const { marketBasketId } = useParams()

	useEffect(() => {
		setActiveTab('ALL')
	}, [directDealId])

	const { data: summaryData } = useQuery({
		queryKey: DirectDealKeys.directDeal(marketBasketId, directDealId, { stocked: activeTab }),
		queryFn: () => DirectDealApi.directDeal(directDealId, { stocked: activeTab }),
	})

	const { data, isLoading } = useQuery({
		queryKey: DirectDealKeys.directDeal(marketBasketId, directDealId, { stocked: activeTab }),
		queryFn: () => DirectDealApi.directDeal(directDealId, { stocked: activeTab }),
	})

	const getListKey = () =>
		DirectDealKeys.list(marketBasketId, { status: statusFilter as DirectDealStatus })

	const handleUpdateDirectDeal = async (
		status: DirectDealStatus.ACCEPTED | DirectDealStatus.REJECTED
	) => {
		await queryClient.cancelQueries({ queryKey: getListKey() })

		// Snapshot current cache state
		const snapshot = snapshotDirectDealCaches(
			queryClient,
			marketBasketId,
			directDealId,
			status,
			activeTab
		)

		// total savings to be added to summary if accepted
		const annualTotalSavings = snapshot.updatedDirectDealData?.summary.annualTotalSavings ?? 0

		// Apply optimistic updates
		updateSummaryCache(queryClient, marketBasketId, status, annualTotalSavings)
		removeFromPendingList(queryClient, marketBasketId, directDealId)
		addToTargetList(
			queryClient,
			marketBasketId,
			directDealId,
			status,
			snapshot.updatedDirectDealData
		)
		updateAllList(queryClient, marketBasketId, directDealId, status, snapshot.updatedDirectDealData)
		updateDetailDealStatus(
			queryClient,
			marketBasketId,
			directDealId,
			status,
			snapshot.updatedDirectDealData,
			activeTab
		)

		onUpdateDirectDeal(null)

		return {
			...snapshot,
			status,
		}
	}

	const handleUpdateError = (
		_err: unknown,
		_vars: unknown,
		context:
			| {
					previousSummaryData: unknown
					previousPendingListData: DirectDealSummary[] | undefined
					previousTargetListData: DirectDealSummary[] | undefined
					updatedDirectDealData: unknown
					status: DirectDealStatus.ACCEPTED | DirectDealStatus.REJECTED
					// formatter is having issues with the indentation here
					// eslint-disable-next-line no-mixed-spaces-and-tabs
			  }
			| undefined
	) => {
		if (!context) return
		restoreCachesFromSnapshot(queryClient, context as any, marketBasketId)
	}

	const handleUpdateSettled = () => {
		queryClient.invalidateQueries({
			queryKey: DirectDealKeys.all(marketBasketId),
		})
	}

	const { mutate: acceptSwitch } = useMutation({
		mutationFn: DirectDealApi.acceptDirectDeal,
		onMutate: () => handleUpdateDirectDeal(DirectDealStatus.ACCEPTED),
		onError: handleUpdateError,
		onSettled: handleUpdateSettled,
	})

	const { mutate: rejectSwitch } = useMutation({
		mutationFn: DirectDealApi.rejectDirectDeal,
		onMutate: () => handleUpdateDirectDeal(DirectDealStatus.REJECTED),
		onError: handleUpdateError,
		onSettled: handleUpdateSettled,
	})

	return (
		<Stack sx={{ width: '100%', height: '100%', overflow: 'hidden', p: 2 }}>
			<Typography variant="h6" mb={2}>
				Selected Manufacturer
			</Typography>

			<Paper
				elevation={2}
				sx={{
					borderRadius: 2,
					flex: 1,
					overflow: 'hidden',
					display: 'flex',
					flexDirection: 'column',
				}}
			>
				<SummaryUI
					summary={summaryData?.summary}
					currency={currency}
					onAccept={() => acceptSwitch(directDealId)}
					onReject={() => rejectSwitch(directDealId)}
				/>

				<Divider />

				<PurchaseListUI
					purchases={data?.purchases ?? []}
					purchaseCounts={summaryData?.purchaseCounts}
					isLoading={isLoading}
					currency={currency}
					activeTab={activeTab}
					onChangeActiveTab={setActiveTab}
					// onCompare={(purchase) => setComparePurchase(purchase)}
				/>
			</Paper>
		</Stack>
	)
}

type SummaryUIProps = {
	summary: DirectDealSummary | undefined
	currency: string
	onAccept: () => void
	onReject: () => void
}

export function SummaryUI({ summary, currency, onAccept, onReject }: SummaryUIProps) {
	const categoryProps = summary ? CategoryProps[summary.category] : null

	if (!summary) {
		return (
			<Stack p={2} spacing={1} data-testid="direct-deal-summary-skeleton">
				<Skeleton variant="text" width="40%" height="2rem" />
				<Skeleton variant="rectangular" width="100%" height={60} />
			</Stack>
		)
	}

	return (
		<>
			<Stack
				direction="row"
				justifyContent="space-between"
				alignItems="center"
				p={2}
				data-testid="direct-deal-summary"
			>
				<Stack direction="row" alignItems="center" spacing={1.5}>
					<Typography variant="body1" fontWeight="bold">
						{summary.manufacturerName}
					</Typography>
					<Chip
						label={categoryProps.label}
						size="small"
						sx={{
							color: categoryProps.color,
							bgcolor: categoryProps.bgcolor,
						}}
					/>
				</Stack>
				<Stack direction="row" spacing={1}>
					{summary.status === DirectDealStatus.PENDING && (
						<>
							<Button
								variant="text"
								color="error"
								startIcon={<FontAwesomeIcon icon={faX} />}
								size="small"
								sx={{ px: 2, py: 1 }}
								onClick={onReject}
							>
								Remove
							</Button>
							<Button
								variant="contained"
								color="success"
								startIcon={<FontAwesomeIcon icon={faCheck} />}
								size="small"
								sx={{ px: 2 }}
								onClick={onAccept}
							>
								Accept
							</Button>
						</>
					)}
				</Stack>
			</Stack>

			<Divider />

			<Stack
				direction="row"
				spacing={3}
				justifyContent="space-between"
				divider={<Divider orientation="vertical" flexItem />}
				sx={{ px: 2 }}
			>
				<Stack sx={{ py: 2, width: '100%' }} alignItems="center" justifyContent="center">
					<Typography variant="body2" fontWeight="bold" color="text.secondary">
						Coverage
					</Typography>
					<Typography variant="h6" color={categoryProps?.color}>
						{numToPercentage(summary.coverage / 100, 0)}
					</Typography>
					<Typography
						variant="body2"
						color="text.primary"
						data-testid="direct-deal-summary-exact-coverage"
					>
						Exact: {numToPercentage((summary.exactCoverage ?? 0) / 100, 0)}
					</Typography>
				</Stack>
				<Stack sx={{ py: 2, width: '100%' }} alignItems="center" justifyContent="center">
					<Typography variant="body2" fontWeight="bold" color="text.secondary">
						Stocked
					</Typography>
					<Typography variant="h6" color={categoryProps.color}>
						{numToPercentage(summary.stocking / 100, 0)}
					</Typography>
				</Stack>
				<Stack sx={{ py: 2, width: '100%' }} alignItems="center" justifyContent="center">
					<Typography variant="body2" fontWeight="bold" color="text.secondary">
						Price Savings
					</Typography>
					<Typography variant="h6" color={categoryProps.color}>
						{numToCurrency(summary.annualPriceSavings, currency, 0)}
					</Typography>
					<Typography
						variant="body2"
						color="text.primary"
						data-testid="direct-deal-summary-exact-price-savings"
					>
						Exact: {numToCurrency(summary.exactAnnualPriceSavings ?? 0, currency, 0)}
					</Typography>
				</Stack>
				<Stack sx={{ py: 2, width: '100%' }} alignItems="center" justifyContent="center">
					<Typography variant="body2" fontWeight="bold" color="text.secondary">
						Rebate Savings
					</Typography>
					<Typography variant="h6" color={categoryProps.color}>
						{numToCurrency(summary.annualRebateSavings, currency, 0)}
					</Typography>
					<Typography
						variant="body2"
						color="text.primary"
						data-testid="direct-deal-summary-exact-rebate-savings"
					>
						Exact: {numToCurrency(summary.exactAnnualRebateSavings ?? 0, currency, 0)}
					</Typography>
				</Stack>
				<Stack sx={{ py: 2, width: '100%' }} alignItems="center" justifyContent="center">
					<Typography variant="body2" fontWeight="bold" color="text.secondary">
						Total Savings
					</Typography>
					<Typography variant="h6" color={categoryProps.color}>
						{numToCurrency(summary.annualTotalSavings, currency, 0)}
					</Typography>
					<Typography
						variant="body2"
						color="text.primary"
						data-testid="direct-deal-summary-exact-total-savings"
					>
						Exact: {numToCurrency(summary.exactAnnualTotalSavings ?? 0, currency, 0)}
					</Typography>
				</Stack>
			</Stack>
		</>
	)
}

type PurchaseListUIProps = {
	purchases: PurchaseRecord[]
	purchaseCounts: DirectDealPurchaseCounts | undefined
	isLoading: boolean
	currency: string
	activeTab: TabOptions
	onChangeActiveTab: (activeTab: TabOptions) => void
	// onCompare: (purchase: PurchaseRecord) => void
}

export function PurchaseListUI({
	purchases,
	purchaseCounts,
	isLoading,
	currency,
	activeTab,
	onChangeActiveTab,
	// onCompare,
}: PurchaseListUIProps) {
	return (
		<Stack
			sx={{ flex: 1, overflow: 'hidden', px: 2, pt: 2 }}
			data-testid="direct-deal-purchase-list"
		>
			<Typography variant="h6">Switch Suggestions</Typography>
			<Tabs
				value={activeTab}
				onChange={(_, value) => {
					onChangeActiveTab(value)
				}}
			>
				<Tab label={`All (${purchaseCounts?.total ?? '--'})`} value="ALL" />
				<Tab label={`Stocked (${purchaseCounts?.stocked ?? '--'})`} value="STOCKED" />
				<Tab label={`Not Stocked (${purchaseCounts?.notStocked ?? '--'})`} value="NOT_STOCKED" />
			</Tabs>

			<Stack spacing={2} sx={{ overflowY: 'auto', flex: 1, mt: 2, pb: 2 }}>
				{isLoading && (
					<>
						<Skeleton variant="rectangular" width="100%" height={72} />
						<Skeleton variant="rectangular" width="100%" height={72} />
						<Skeleton variant="rectangular" width="100%" height={72} />
					</>
				)}
				{!isLoading && purchases.length === 0 && (
					<Stack flex={1} alignItems="center" justifyContent="center">
						<Typography variant="h6" color="text.secondary">
							No purchases found
						</Typography>
					</Stack>
				)}
				{!isLoading &&
					purchases.map((purchase) => (
						<PurchaseCard
							key={purchase.id}
							purchase={purchase}
							currency={currency}
							// onCompare={() => onCompare(purchase)}
						/>
					))}
			</Stack>
		</Stack>
	)
}
